import React, { useEffect } from "react";

import _ from 'lodash';
import cx from 'classnames';

import { useSelector, useDispatch } from "react-redux";
import { createCollectionList, deleteCollectionList, fetchCollectionLists, fetchCollections, invalidateCollectionListCreationState } from "../../../../redux/actions/collections";

import styles from "./list.module.scss";
import { NavLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Collection, CollectionList } from "../../../../types/collections";
import Slider from "../../../../components/slider";
import Button from "../../../../components/button";
import { AutoGrowInput, Checkbox, Input } from "../../../../components/input";
import InfiniteScroll from "../../../../components/infiniteScroll";
import { CollectionsContext, useCollections } from "../..";
import { CollectionCard, PlaceholderCollectionCard } from "../card";
import Reveal from "../../../../components/animation";
import { useDroppable } from "@dnd-kit/core";
import { DEFAULT_COLLECTION_LIST, USER_DEFINED } from "../../../../constants/collections";
import { ScrollableContainer } from "src/components/scroller";
import { useCollectionsAdoption } from "src/redux/actions/adoption/collection";
import { collection } from "src/localization/english/keywords";


interface CollectionListsProps {
  editMode: 'create' | 'rename' | 'delete' | 'clone' | null | undefined;
}

const CollectionLists: React.FC<CollectionListsProps> = ({ editMode }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { folderId } = useParams();
  const collectionLists = useSelector((state) => (_.get(state, 'collections.lists.items') || []) as CollectionList[]);
  const loading = useSelector((state) => (_.get(state, 'collections.lists.loading')));

  const { edits, onEditChanges, onEditModeChange } = React.useContext(CollectionsContext);
  const [deleteList, setDeleteList] = React.useState<CollectionList | null>(null);

  useEffect(() => {
    dispatch(fetchCollectionLists());
  }, []);

  useEffect(() => {
    if (edits && edits.length > 0 && editMode === 'delete') {
      const lists = _.filter(edits, (edit) => {
        return edit.type === 'collection_list' && edit.value !== null;
      });

      if (lists.length > 0) {
        setDeleteList(lists[0].value as CollectionList);
      }
    }
  }, [edits]);

  useEffect(() => {
    if(folderId){
      const element = document.querySelector(`#list_${folderId}`);
      element?.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
    }
  }, [folderId]);

  const handleDeletionClose = (collectionList: CollectionList) => {
    setDeleteList(null);
    if (onEditChanges) {
      onEditChanges({
        id: collectionList.id,
        type: 'collection_list',
        value: null
      });
    }
    onEditModeChange(null);
  }

  const handleDeletionFinish = (collectionList: CollectionList) => {
    handleDeletionClose(collectionList);
    if (collectionList.id === folderId) {
      navigate('/collections');
    }
  }

  return (
    <div className={cx(styles.lists, {
      [styles.renaming]: editMode === 'rename',
    })}>
      <AddCollectionListButton />
      <ScrollableContainer distance={300}>
        {DEFAULT_COLLECTION_LIST.map((list, i) => {
          const active = list.id === folderId || (list.id === 'all' && !folderId);
          return <SmartCollectionListButton key={i} item={list} active={active} />
        })}
        {collectionLists.map((list, i) => {
          const active = list.id === folderId;
          return <CollectionListButton key={i} item={list} editMode={editMode} active={active} />
        })}

        {loading &&
          _.times(2).map((i) => (
            <PlaceholderCollectionList key={i} />
          ))
        }
      </ScrollableContainer>

      {deleteList &&
        <DeleteCollectionListConfirmationDialog
          show={deleteList !== null}
          collectionList={deleteList}
          onFinish={handleDeletionFinish}
          onClose={handleDeletionClose}
        />
      }
    </div>
  );
}

interface CollectionListButtonProps {
  item: CollectionList;
  editMode: 'create' | 'rename' | 'delete' | 'clone' | null | undefined;
  active: boolean;
}

const CollectionListButton: React.FC<CollectionListButtonProps> = ({ item, active, editMode }) => {
  const rename = editMode === 'rename';
  const deletion = editMode === 'delete';

  const [updatedName, setUpdatedName] = React.useState(item.name);
  const { onEditChanges } = React.useContext(CollectionsContext);

  const { trackCollectionsFiltered } = useCollectionsAdoption();

  //Drag Drop
  const { setNodeRef, isOver } = useDroppable({
    id: item.id,
    disabled: active || !!editMode,
    data: {
      list: item,
      accepts: ['collection'],
    },
  });

  useEffect(() => {
    setUpdatedName(item.name);
  }, [rename])

  const handleRename = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUpdatedName(e.target.value);
    if (e.target.value && onEditChanges) {
      onEditChanges({
        id: item.id,
        type: 'collection_list',
        value: e.target.value
      });
    }
  }

  const handleDeletion = () => {
    if (onEditChanges) {
      onEditChanges({
        id: item.id,
        type: 'collection_list',
        value: item
      });
    }
  }

  const handleClick = () => {
    trackCollectionsFiltered(item.name);
  }

  const [searchParams] = useSearchParams();
  const query = searchParams.get('query');

  const url = query ? `/collections/folders/${item.id}?query=${query}` : `/collections/folders/${item.id}`;

  return (
    <NavLink ref={setNodeRef} id={`list_${item.id}`} to={url}
      className={cx(styles.list, {
        [styles.active]: active,
        [styles.dropOver]: isOver,
      })} onClick={handleClick}>
      {!(active && rename) && 
        <div className={cx(styles.name)}>{item.name}</div>
      }

      {active && rename &&
        <AutoGrowInput
          className={cx(styles.input)}
          placeholder={item.name}
          value={updatedName}
          onChange={handleRename}
        />
      }

      {deletion &&
        <div className={cx(styles.delete)} onClick={handleDeletion}>
          <i className={cx(styles.icon, "paperflite-close")}></i>
        </div>
      }
    </NavLink>
  );
}

const SmartCollectionListButton: React.FC<{ item: CollectionList, active: boolean }> = ({ item, active }) => {
  const id = item.id;
  const [searchParams] = useSearchParams();
  const query = searchParams.get('query');
  
  const url = id === 'all' 
    ? (query ? `/collections?query=${query}` : '/collections') 
    : `/collections/folders/${id}${query ? `?query=${query}` : ''}`;

  const { trackCollectionsFiltered } = useCollectionsAdoption();

  const { setNodeRef, isOver } = useDroppable({
    id: item.id,
    disabled: active || id !== 'all',
    data: {
      list: item,
      accepts: ['collection'],
    },
  });

  const handleClick = () => {
    trackCollectionsFiltered(item.name);
  }

  return (
    <NavLink ref={setNodeRef} to={url} className={cx(styles.list, styles.smart, {
      [styles.active]: active,
      [styles.dropOver]: isOver,
    })} onClick={handleClick}>
      <div className={cx(styles.name)}>{item.name}</div>
    </NavLink>
  );
}

const PlaceholderCollectionList: React.FC<object> = () => {
  return (
    <div className={cx(styles.list, styles.placeholder)}>
      <div className={cx(styles.name, styles.block)}></div>
    </div>
  );
}


const AddCollectionListButton: React.FC<object> = () => {
  const [showAddList, setShowAddList] = React.useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { trackCreateFolderClicked, trackFolderCreated } = useCollectionsAdoption();

  const handleClick = () => {
    trackCreateFolderClicked();
    setShowAddList(true);
  }

  const handleFinish = (collectionList: CollectionList) => {
    handleCancel();
    if (collectionList) {
      trackFolderCreated(collectionList.name);
      navigate(`/collections/folders/${collectionList.id}`);
    }
  }

  const handleCancel = () => {
    setShowAddList(false);
    dispatch(invalidateCollectionListCreationState());
  }

  return (
    <>
      <div className={styles.addListButton} onClick={handleClick}>
        <i className={cx(styles.icon, "paperflite-add")}></i>
        <div className={cx(styles.name, styles.add)}>New Folder</div>
      </div>

      <AddCollectionListDialog
        show={showAddList}
        onFinish={handleFinish}
        onClose={handleCancel}
      />
    </>
  );
}

interface AddCollectionListDialogProps {
  show: boolean;
  // eslint-disable-next-line no-unused-vars
  onFinish: (collectionList: CollectionList) => void;
  onClose: () => void;
}

const AddCollectionListDialog: React.FC<AddCollectionListDialogProps> = ({ show, onFinish, onClose }) => {
  const dispatch = useDispatch();
  const [name, setName] = React.useState('');
  const [selectedCollections, setSelectedCollections] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [showCollections, setShowCollections] = React.useState(false);
  const { collections, loading, hasMoreItems, nextPage } = useCollections(undefined, null);

  const creating = useSelector((state) => (_.get(state, `collections.lists.creation.creating`) || false));
  const createdList = useSelector((state) => {
    const finished = _.get(state, `collections.lists.creation.created`) || false;
    if (finished) {
      return _.get(state, `collections.lists.creation.item`);
    }

    return null;
  });

  useEffect(() => {
    if(show){
      fetchCollectionsForPage(0);
    }

    setName('');
    setSelectedCollections([]);
    setError(null);
    setShowCollections(false);
  }, [show]);

  useEffect(() => {
    if (createdList) {
      onFinish(createdList);
    }
  }, [createdList]);

  useEffect(() => {
    if(!name) {
      setShowCollections(false);
    }
    
    const timeRef = setTimeout(() => {
      setShowCollections(!!name);
    }, 1000);

    return () => {
      clearTimeout(timeRef);
    }
  }, [name]);

  const fetchCollectionsForPage = (page: number) => {
    const request = {
      list: undefined,
      page: page,
      limit: 20,
      sortBy: 'name',
      sortDirection: 'asc',
    };

    dispatch(fetchCollections(request));
  }

  const handleLoadMoreCollections = () => {
    if (nextPage) {
      fetchCollectionsForPage(nextPage);
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setError(null);
  }

  const handleCollectionSelection = (data: any) => {
    const id = data.id;
    const selected = !!data.value;

    setSelectedCollections((prev) => {
      if (selected) {
        return _.uniq([...prev, id]);
      }

      return _.filter(prev, (item) => item !== id);
    });
  }

  const handleCancel = () => {
    onClose();
  }

  const validateForm = () => {
    if (_.isEmpty(name) || _.isEmpty(_.trim(name))) {
      return 'Please provide a valid name';
    }

    if (_.size(name) > 200) {
      return 'Folder name cannot be greater than 200 characters';
    }

    return null;
  }

  const handleConfirm = () => {
    const e = validateForm();
    if (e) {
      setError(e);
      return
    }

    dispatch(createCollectionList({
      name: name,
      collections: selectedCollections
    }));
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleConfirm();
  }

  return (
    <Slider.Dialog width="300" className={styles.addCollectionListDialog} show={show} onClose={onClose}>
      <Slider.Header
        title="creating new folder"
        message="Time for a fresh folder! What do you want to call it?"
      >
        <form className={styles.addListForm} onSubmit={handleSubmit}>
          <Input autoFocus className={styles.input} value={name} placeholder="Add a folder name..." onChange={handleChange} />
          <Reveal show={!!error}>
            <div className={styles.error}>
              <i className="paperflite-danger"></i>
              {error}
            </div>
          </Reveal>
        </form>
      </Slider.Header>
      <Slider.Body>
        <Reveal show={showCollections}>
          <p className={styles.description}>Drop your existing collections into this folder...</p>
          <InfiniteScroll className={styles.grid}
            loading={loading}
            hasMore={hasMoreItems}
            loadMore={handleLoadMoreCollections}
            loader={() => {
              return _.times(_.size(collections) == 0 ? 4 : 1).map((i) => (
                <PlaceholderCollectionCard key={i} />
              ));
            }}
          >
            {collections.filter((collection: Collection) => collection.type === USER_DEFINED)
            .map((collection: Collection, i) => {
              const selected = selectedCollections.includes(collection.id || '');

              return <CollectionCard key={i} item={collection}
                editMode={'selection'}
                selected={selected}
                onEditChanges={handleCollectionSelection}
              />
            })}
          </InfiniteScroll>
        </Reveal>
      </Slider.Body>
      <Slider.Actions>
        <Button icon="paperflite-tick"
          text={creating ? 'Creating...' : 'Create'}
          variant='primary'
          disabled={creating || !name}
          onClick={handleConfirm}
        />
        <Button text="Cancel"
          variant='secondary'
          disabled={creating}
          onClick={handleCancel}
        ></Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
}

interface DeleteCollectionListConfirmationDialogProps {
  show: boolean;
  collectionList: CollectionList;
  // eslint-disable-next-line no-unused-vars
  onClose: (collectionList: CollectionList) => void;
  // eslint-disable-next-line no-unused-vars
  onFinish: (collectionList: CollectionList) => void;
}

const DeleteCollectionListConfirmationDialog: React.FC<DeleteCollectionListConfirmationDialogProps> = ({
  show, collectionList, onClose, onFinish
}) => {
  const dispatch = useDispatch();
  const [deleteCollections, setDeleteCollections] = React.useState(false);
  const { collections, loading, hasMoreItems, nextPage, total } = useCollections(collectionList.id, null);
  const deleting = useSelector((state) => (_.get(state, `collections.lists.deletion['${collectionList.id}'].deleting`) || false));
  const deleted = useSelector((state) => (_.get(state, `collections.lists.deletion['${collectionList.id}'].deleted`) || false));

  useEffect(() => {
    if (show) {
      fetchCollectionsForPage(0);
    }
  }, [show]);

  const fetchCollectionsForPage = (page: number) => {
    const request = {
      list: collectionList.id,
      page: page,
      limit: 20,
      sortBy: 'name',
      sortDirection: 'asc',
    };

    dispatch(fetchCollections(request));
  }

  useEffect(() => {
    if (deleted) {
      onFinish(collectionList);
    }
  }, [deleted]);

  const handleLoadMoreCollections = () => {
    if (nextPage) {
      fetchCollectionsForPage(nextPage);
    }
  }

  const handleClose = () => {
    onClose(collectionList);
  }

  const handleConfirm = () => {
    dispatch(deleteCollectionList({
      id: collectionList.id,
      deleteCollections: deleteCollections
    }));
  }

  return (
    <Slider.Dialog className={styles.deleteConfirmDialog} width="300" show={show} onClose={handleClose}>
      <Slider.Header
        title="deleting folder..."
        message={collectionList.name}
      >
        <div className={styles.checkboxContainer}>
          <Checkbox label={`Delete all collections in this folder? Or just drag and drop them somewhere else if you want to keep them around. Decisions, decisions!`}
            checked={deleteCollections}
            onChange={(value) => setDeleteCollections(value)}
          />
        </div>
      </Slider.Header>
      <Slider.Body>
        {deleteCollections &&
          <InfiniteScroll className={styles.grid}
            loading={loading}
            hasMore={hasMoreItems}
            loadMore={handleLoadMoreCollections}
            loader={() => {
              return _.times(_.size(collections) == 0 ? 4 : 1).map((i) => (
                <PlaceholderCollectionCard key={i} />
              ));
            }}
          >
            {collections.filter((collection: Collection) => collection.canDelete).map((collection, i) => {
              return <CollectionCard key={i} item={collection} readonly />
            })}
          </InfiniteScroll>
        }
      </Slider.Body>
      <Slider.Actions>
        <Button icon="paperflite-tick"
          text={deleting ? 'Deleting...' : 'Confirm'}
          variant='primary'
          disabled={deleting}
          onClick={handleConfirm}
        />
        <Button text="Cancel"
          variant='secondary'
          disabled={deleting}
          onClick={handleClose}
        />
      </Slider.Actions>
    </Slider.Dialog>
  )
}

export { CollectionLists };